import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api.js";
import { centsToDisplay } from "../lib/money.js";

// Print-styled invoice — no app chrome. On iPhone: Print → pinch out → share
// the PDF. On the Mac: ⌘P → Save as PDF. The toolbar hides itself in print.
export default function InvoicePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api(`/api/invoices/${id}`)
      .then((d) => setInv(d.invoice))
      .catch(() => setErr("Invoice not found."));
  }, [id]);

  if (err) return <p className="p-8 text-sm text-red-600">{err}</p>;
  if (!inv) return <p className="p-8 text-center text-sm text-slate-400">Loading…</p>;

  return (
    <div className="min-h-full bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-3 print:hidden">
        <button onClick={() => navigate(-1)} className="text-sm font-semibold text-navy-700">
          ‹ Back
        </button>
        <button onClick={() => window.print()} className="rounded-xl bg-navy-700 px-5 py-2 text-sm font-semibold text-white">
          🖨️ Print / Save PDF
        </button>
      </div>

      <div className="mx-auto max-w-2xl px-8 py-10">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-2xl font-bold" style={{ color: "#234E70" }}>
              AI Engines
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Marion, Indiana · aiengines.work
              <br />
              People-first AI. Human-centered. Built for local impact.
            </p>
          </div>
          <div className="text-right">
            <p className="text-xl font-semibold tracking-wide text-slate-800">INVOICE</p>
            <p className="mt-1 text-sm text-slate-500">{inv.number}</p>
          </div>
        </div>

        <div className="mt-8 flex justify-between text-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bill to</p>
            <p className="mt-1 font-medium text-slate-800">{inv.contact_company || inv.contact_name || "—"}</p>
            {inv.contact_company && inv.contact_name && <p className="text-slate-600">Attn: {inv.contact_name}</p>}
            {inv.contact_address && <p className="whitespace-pre-line text-slate-600">{inv.contact_address}</p>}
            {inv.contact_email && <p className="text-slate-600">{inv.contact_email}</p>}
          </div>
          <div className="text-right">
            <p className="text-slate-600">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Issued</span> {inv.issue_date}
            </p>
            {inv.due_date && (
              <p className="mt-1 text-slate-600">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Due</span> {inv.due_date}
              </p>
            )}
          </div>
        </div>

        <table className="mt-8 w-full text-sm">
          <thead>
            <tr className="border-b-2" style={{ borderColor: "#234E70" }}>
              <th className="pb-2 text-left font-semibold text-slate-700">Description</th>
              <th className="pb-2 text-right font-semibold text-slate-700">Qty</th>
              <th className="pb-2 text-right font-semibold text-slate-700">Rate</th>
              <th className="pb-2 text-right font-semibold text-slate-700">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l) => (
              <tr key={l.id} className="border-b border-slate-100">
                <td className="py-2.5 pr-4 text-slate-800">{l.description}</td>
                <td className="py-2.5 text-right text-slate-600">{l.qty}</td>
                <td className="py-2.5 text-right text-slate-600">{centsToDisplay(l.unit_cents)}</td>
                <td className="py-2.5 text-right font-medium text-slate-800">{centsToDisplay(Math.round(l.qty * l.unit_cents))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex justify-end">
          <div className="w-56 rounded-xl px-4 py-3" style={{ backgroundColor: "#234E7014" }}>
            <div className="flex justify-between text-base font-bold" style={{ color: "#234E70" }}>
              <span>Total due</span>
              <span>{centsToDisplay(inv.total_cents)}</span>
            </div>
          </div>
        </div>

        {inv.payment_link && (
          <div className="mt-6">
            <a
              href={inv.payment_link}
              className="inline-block rounded-lg px-4 py-2 text-sm font-semibold text-white no-underline"
              style={{ backgroundColor: "#234E70" }}
            >
              Pay online →
            </a>
            <p className="mt-1 break-all text-xs text-slate-500">{inv.payment_link}</p>
          </div>
        )}

        {inv.notes && <p className="mt-8 whitespace-pre-line text-sm text-slate-600">{inv.notes}</p>}
        <p className="mt-10 text-center text-xs text-slate-400">Thank you for your business.</p>
      </div>
    </div>
  );
}
